/**
 * What an estimate's parts list was built from, kept with the estimate.
 *
 * The check against the quote rebuilds the parts list an estimate implies and
 * compares a material order with it (ADR 0156). Rebuilt from today's template,
 * catalogue, prices and roof, it drifts the moment any of them is edited: a
 * vent line added to a template, a bundle's coverage corrected, a price moved.
 * The order did not change, but the check says it differs — and nobody can
 * tell that from a rep's edit. Production showed exactly that: job 1040's
 * order was generated at 237 bundles for 97.22 squares, and a day later the
 * same squares gave 292.
 *
 * So generating an estimate freezes its inputs: the template and its lines,
 * the products those lines name (and their galvalume twins), those products'
 * prices, and the roof as it was measured. The check reads the frozen copy
 * where there is one. Anything the snapshot does not hold — a product a change
 * order adds, a product the estimate was later changed to sell — still comes
 * from today's catalogue, because nothing froze it.
 *
 * An estimate can be more than one package: a shingle house with a metal
 * porch is the house's package on the roof less the porch, and the porch's own
 * package on the porch, bought from the porch package's supplier. Each of
 * those areas is frozen too, beside the main one (ADR 0166), so the porch's
 * order is checked rather than read as a different recipe.
 */

import type {
  GeneratorProduct,
  RoofMeasurements,
  RoofTemplateItem,
} from "./generator.js";
import type { PricedSupplier, ProductPrice } from "./supplierPricing.js";
import type { PlanProduct, PlanTemplate } from "./templateLines.js";

/** The shape written to `estimates.generation_snapshot`. */
export interface GenerationSnapshot {
  /** Bumped if the shape changes; an unknown version is not read. */
  version: 1;
  /**
   * The template it was generated from. Deleting that template nulls the
   * estimate's `roof_template_id`; this is how the check still knows which
   * template its orders were built from.
   */
  templateId: string;
  template: PlanTemplate;
  templateItems: RoofTemplateItem[];
  products: (GeneratorProduct & PlanProduct)[];
  prices: ProductPrice[];
  suppliers: PricedSupplier[];
  roofs: RoofMeasurements[];
  /**
   * The areas in another material, each generated from its own template on
   * its own roof. Their products and prices are in `products` and `prices`
   * with the main package's. Absent on an estimate of one package, and on
   * every estimate generated before 2026-09-19.
   */
  areas?: GenerationArea[];
}

/** One area of an estimate in another material: a metal porch on a shingle house. */
export interface GenerationArea {
  /** What the builder called it: "Porch". */
  label: string;
  templateId: string;
  template: PlanTemplate;
  templateItems: RoofTemplateItem[];
  /** The area's own measurements, not the house's. */
  roofs: RoofMeasurements[];
  /** Who the area's parts are bought from: its own package's supplier. */
  supplierId: string | null;
}

/** The inputs the check is built from, frozen or live. */
export interface QuoteSources<
  P extends GeneratorProduct & PlanProduct = GeneratorProduct & PlanProduct,
> {
  /** The estimate's `roof_template_id`. */
  templateId: string | null;
  template: PlanTemplate | undefined;
  templateItems: readonly RoofTemplateItem[];
  catalogue: readonly P[];
  measurements: readonly RoofMeasurements[];
  prices: readonly ProductPrice[];
  suppliers: readonly PricedSupplier[];
  /**
   * The estimate's areas in another material. Only a frozen copy knows them:
   * nothing else records which template each area was generated from.
   */
  areas?: readonly GenerationArea[];
}

/**
 * Freeze what a generation read.
 *
 * Only what the lines touch: the products the template names, their galvalume
 * twins (a galvalume answer swaps each painted part for its twin), and the
 * headline. The whole catalogue would be most of a megabyte on every estimate.
 */
export function generationSnapshot(input: {
  template: PlanTemplate & { id: string };
  templateItems: readonly RoofTemplateItem[];
  catalogue: readonly (GeneratorProduct & PlanProduct)[];
  prices: readonly ProductPrice[];
  suppliers: readonly PricedSupplier[];
  roofs: readonly RoofMeasurements[];
}): GenerationSnapshot {
  const byId = new Map(input.catalogue.map((p) => [p.id, p]));
  const named = new Set<string>();
  for (const item of input.templateItems) {
    if (item.product_id) named.add(item.product_id);
  }
  if (input.template.headline_product_id) {
    named.add(input.template.headline_product_id);
  }
  for (const id of [...named]) {
    const twin = byId.get(id)?.galvalume_twin_id;
    if (twin) named.add(twin);
  }

  return {
    version: 1,
    templateId: input.template.id,
    template: { ...input.template },
    templateItems: input.templateItems.map((item) => ({ ...item })),
    products: input.catalogue
      .filter((p) => named.has(p.id))
      .map((p) => ({ ...p })),
    prices: input.prices
      .filter((row) => named.has(row.product_id))
      .map((row) => ({ ...row })),
    suppliers: input.suppliers.map((s) => ({
      id: s.id,
      price_list_id: s.price_list_id ?? null,
    })),
    roofs: input.roofs.map((roof) => ({ ...roof })),
  };
}

/**
 * The main package's copy, with the estimate's areas in another material
 * frozen beside it.
 *
 * Each area is frozen the same way as the main package, by `generationSnapshot`
 * over its own template and roof. Their products, prices and suppliers are
 * merged into the main copy's, which is where the check reads a price from
 * whichever part it is pricing; a product two parts share is held once.
 */
export function withAreas(
  main: GenerationSnapshot,
  areas: readonly {
    label: string;
    supplierId: string | null;
    snapshot: GenerationSnapshot;
  }[],
): GenerationSnapshot {
  if (areas.length === 0) return main;
  const products = new Map(main.products.map((p) => [p.id, p]));
  const prices = new Map(
    main.prices.map((row) => [`${row.price_list_id}:${row.product_id}`, row]),
  );
  const suppliers = new Map(main.suppliers.map((s) => [s.id, s]));
  for (const { snapshot } of areas) {
    for (const p of snapshot.products)
      if (!products.has(p.id)) products.set(p.id, p);
    for (const row of snapshot.prices) {
      const key = `${row.price_list_id}:${row.product_id}`;
      if (!prices.has(key)) prices.set(key, row);
    }
    for (const s of snapshot.suppliers)
      if (!suppliers.has(s.id)) suppliers.set(s.id, s);
  }
  return {
    ...main,
    products: [...products.values()],
    prices: [...prices.values()],
    suppliers: [...suppliers.values()],
    areas: areas.map(({ label, supplierId, snapshot }) => ({
      label,
      templateId: snapshot.templateId,
      template: snapshot.template,
      templateItems: snapshot.templateItems,
      roofs: snapshot.roofs,
      supplierId,
    })),
  };
}

const isSnapshot = (value: unknown): value is GenerationSnapshot =>
  typeof value === "object" &&
  value !== null &&
  (value as { version?: unknown }).version === 1 &&
  Array.isArray((value as { templateItems?: unknown }).templateItems);

/**
 * The inputs to check against: the estimate's frozen ones where it has them,
 * today's otherwise.
 *
 * Frozen products and prices win over today's for the same product; every
 * other product and price is today's, so a change order or a later headline
 * product can still be priced. `frozen` says which the check read, so the
 * screen can say so.
 *
 * `templateId` is the estimate's own where it still has one, and the frozen
 * copy's where its template was deleted since: the copy holds everything the
 * check needs, so a deleted template does not make the estimate uncheckable.
 */
export function quoteSources<P extends GeneratorProduct & PlanProduct>(
  snapshot: unknown,
  live: QuoteSources<P>,
): QuoteSources<P> & { frozen: boolean } {
  if (!isSnapshot(snapshot)) return { ...live, frozen: false };

  const frozenIds = new Set(snapshot.products.map((p) => p.id));
  const frozenSuppliers = new Set(snapshot.suppliers.map((s) => s.id));
  return {
    templateId: live.templateId ?? snapshot.templateId ?? null,
    template: snapshot.template,
    templateItems: snapshot.templateItems,
    catalogue: [
      ...(snapshot.products as P[]),
      ...live.catalogue.filter((p) => !frozenIds.has(p.id)),
    ],
    measurements: snapshot.roofs,
    prices: [
      ...snapshot.prices,
      ...live.prices.filter((row) => !frozenIds.has(row.product_id)),
    ],
    suppliers: [
      ...snapshot.suppliers,
      ...live.suppliers.filter((s) => !frozenSuppliers.has(s.id)),
    ],
    areas: Array.isArray(snapshot.areas) ? snapshot.areas : [],
    frozen: true,
  };
}

/** Which roof an order generated From Measurements is sized against. */
export interface RoofsForOrder {
  /** Named as the layout (or the area) they came from, where they say. */
  roofs: (RoofMeasurements & { name?: string | null })[];
  /**
   * `measured`: the job's measurements as they are today, the whole roof.
   * `rest_of_roof`: the roof less the estimate's areas in another material,
   * as the estimate was generated. `area`: one of those areas, on its own.
   */
  source: "measured" | "rest_of_roof" | "area";
  /** For `area`: what the builder called it. */
  areaLabel?: string;
}

/**
 * The roof an order built From Measurements on the orders screen is sized
 * against (ADR 0184, amending ADR 0150).
 *
 * An estimate of one package generates against the job's measurements as they
 * are today, as it always has. An estimate with areas in another material (a
 * metal porch on a shingle house) kept what it was generated from (ADR 0166),
 * so the orders screen reads that instead of the whole roof:
 *
 *   - the template of one of its areas generates against that area's own
 *     measurements;
 *   - any other template generates against the rest of the roof, the roof
 *     less every area, as frozen when the estimate was generated.
 *
 * The frozen rest of the roof, not a fresh subtraction from today's layouts:
 * the areas were taken off a particular layout column by column, with the
 * flat squares, ridge vent and steep bands adjusted (`roofsWithout`), and the
 * copy is that answer. It is also what the quote check compares the order
 * with, so an order regenerated here matches the quote instead of being
 * reported as over-ordered.
 *
 * A template measured on the walls (siding) is sized on `measured`, the
 * walls, whatever the copy holds. The copy freezes roofs only: the areas are
 * parts of the roof, and a siding estimate is generated against the walls
 * alone with no areas. Reading the frozen roof for a siding template resolved
 * every wall quantity to 0 (#604).
 */
export function roofsForOrder(
  snapshot: unknown,
  templateId: string | null | undefined,
  measured: readonly RoofMeasurements[],
  surface: "roof" | "walls" = "roof",
): RoofsForOrder {
  if (surface === "walls" || !isSnapshot(snapshot) || !snapshot.areas?.length) {
    return { roofs: measured.map((r) => ({ ...r })), source: "measured" };
  }
  if (templateId && templateId !== snapshot.templateId) {
    const areas = snapshot.areas.filter((a) => a.templateId === templateId);
    if (areas.length > 0) {
      return {
        roofs: areas.flatMap((area) => area.roofs.map((r) => ({ ...r }))),
        source: "area",
        areaLabel: areas.map((area) => area.label).join(" and "),
      };
    }
  }
  return {
    roofs: snapshot.roofs.map((r) => ({ ...r })),
    source: "rest_of_roof",
  };
}
